import {
  doc,
  setDoc,
  getDoc,
  serverTimestamp,
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  Timestamp,
} from 'firebase/firestore';
import { db, auth } from './config';
import type { OnboardingData, CustomProgram } from '../../stores/onboardingStore';
import type { SobrietyDate, SobrietyDateFormData } from '../../types/user';

/**
 * User Profile Firestore Service
 *
 * Handles saving and retrieving user profile and onboarding data
 */

export interface UserProfile {
  uid: string;
  email: string;
  firstName: string;
  lastInitial: string; // Just the first letter for anonymity (12-step tradition)
  phone?: string; // Optional phone number for account recovery and notifications
  location?: string; // Optional location for meeting finder and resources
  selectedPrograms: string[];
  customPrograms?: CustomProgram[]; // Add custom programs to profile
  sobrietyDates: Array<{
    label: string;
    startDate: Date;
    definition: string;
    programId: string;
  }>;
  hasSponsor: boolean;
  sponsorInviteCode?: string;
  notificationPreferences: {
    checkInReminderEnabled: boolean;
    checkInReminderTime: string;
    nightlyReviewReminderEnabled: boolean;
    nightlyReviewReminderTime: string;
    pushMilestones: boolean;
    pushSponsorMessages: boolean;
    pushDailyReflection: boolean;
    pushMeetings: boolean;
    emailWeeklyDigest: boolean;
    emailSponsorUpdates: boolean;
  };
  // Root-level fields for Cloud Function scheduled queries
  timezone?: string;
  checkInReminderEnabled?: boolean;
  checkInReminderUTCHour?: number;
  checkInReminderUTCMinute?: number;
  nightlyReminderEnabled?: boolean;
  nightlyReminderUTCHour?: number;
  nightlyReminderUTCMinute?: number;
  // Activity tracking preferences
  weekStartDay?: number;              // 0=Sunday … 6=Saturday
  activityTrackingEnabled?: boolean;  // hides ActivityWidget when false (default true)
  categoryOrder?: string[];           // ordered ActivityCategory values
  // Subscription & trial
  trialEnd?: number;          // Unix timestamp ms — trial expires at this time
  isPremium?: boolean;        // true when Stripe subscription active
  foundingMember?: boolean;   // true if subscribed at $10/year founding rate
  stripeCustomerId?: string;  // Stripe customer ID for portal access
  onboardingCompletedAt: Date;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Save user profile and onboarding data to Firestore
 */
export async function saveUserProfile(
  userId: string,
  email: string,
  onboardingData: OnboardingData
): Promise<void> {
  if (!auth.currentUser) throw new Error('Not authenticated');

  const userRef = doc(db, 'users', userId);

  const profileData: Partial<UserProfile> = {
    uid: userId,
    email,
    firstName: onboardingData.profile?.firstName || '',
    lastInitial: onboardingData.profile?.lastInitial || '',
    selectedPrograms: onboardingData.selectedPrograms,
    customPrograms: onboardingData.customPrograms || [], // Save custom programs
    sobrietyDates: onboardingData.sobrietyDates.map((sd) => ({
      label: sd.label,
      startDate: sd.startDate,
      definition: sd.definition,
      programId: sd.programId,
    })),
    hasSponsor: onboardingData.hasSponsor ?? false,
    // Firestore doesn't accept undefined - use null or omit
    ...(onboardingData.sponsorInviteCode ? { sponsorInviteCode: onboardingData.sponsorInviteCode } : {}),
    notificationPreferences: onboardingData.notifications || {
      checkInReminderEnabled: true,
      checkInReminderTime: '09:00',
      nightlyReviewReminderEnabled: true,
      nightlyReviewReminderTime: '21:00',
      pushMilestones: true,
      pushSponsorMessages: true,
      pushDailyReflection: true,
      pushMeetings: true,
      emailWeeklyDigest: true,
      emailSponsorUpdates: true,
    },
    onboardingCompletedAt: new Date(),
    updatedAt: new Date(),
  };

  // Check if document exists
  const docSnap = await getDoc(userRef);

  if (!docSnap.exists()) {
    // New user - set createdAt and 30-day trial
    await setDoc(userRef, {
      ...profileData,
      trialEnd: Date.now() + 30 * 24 * 60 * 60 * 1000,
      isPremium: false,
      foundingMember: false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      onboardingCompletedAt: serverTimestamp(),
    });
  } else {
    // Existing user - merge with existing data
    await setDoc(
      userRef,
      {
        ...profileData,
        updatedAt: serverTimestamp(),
        onboardingCompletedAt: serverTimestamp(),
      },
      { merge: true }
    );
  }

  // Save custom programs to fellowshipSuggestions collection for analytics
  if (onboardingData.customPrograms && onboardingData.customPrograms.length > 0) {
    for (const program of onboardingData.customPrograms) {
      try {
        await saveCustomProgramSuggestion(program, userId);
      } catch (error) {
        console.error('Error saving custom program suggestion:', program.name, error);
        // Don't throw - continue even if suggestion save fails
      }
    }
  }

  console.log('✅ User profile saved to Firestore:', userId);
}

/**
 * Get user profile from Firestore
 */
export async function getUserProfile(userId: string): Promise<UserProfile | null> {
  if (!auth.currentUser) throw new Error('Not authenticated');

  const userRef = doc(db, 'users', userId);
  const docSnap = await getDoc(userRef);

  if (docSnap.exists()) {
    return docSnap.data() as UserProfile;
  }

  return null;
}

/**
 * Update user profile fields
 */
export async function updateUserProfile(
  userId: string,
  updates: Partial<UserProfile>
): Promise<void> {
  if (!auth.currentUser) throw new Error('Not authenticated');

  const userRef = doc(db, 'users', userId);

  await setDoc(
    userRef,
    {
      ...updates,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );

  console.log('✅ User profile updated:', userId);
}

/**
 * Save a custom program suggestion to the fellowshipSuggestions collection
 * Increments user count if program already exists
 */
export async function saveCustomProgramSuggestion(
  program: CustomProgram,
  userId: string
): Promise<string> {
  if (!auth.currentUser) throw new Error('Not authenticated');

  const suggestionsRef = collection(db, 'fellowshipSuggestions');

  // Check if this program already exists by abbreviation
  const existingQuery = query(
    suggestionsRef,
    where('abbreviation', '==', program.abbreviation.toUpperCase())
  );
  const existingSnapshot = await getDocs(existingQuery);

  if (!existingSnapshot.empty) {
    // Program exists - increment user count
    const existingDoc = existingSnapshot.docs[0];
    await updateDoc(existingDoc.ref, {
      userCount: (existingDoc.data().userCount || 1) + 1,
      lastUsedAt: Timestamp.now(),
    });
    console.log('✅ Incremented user count for existing custom program:', program.abbreviation);
    return existingDoc.id;
  }

  // Create new suggestion
  const newSuggestion = {
    name: program.name,
    abbreviation: program.abbreviation.toUpperCase(),
    description: program.description || '',
    website: program.website || null,
    createdBy: userId,
    createdAt: Timestamp.now(),
    userCount: 1,
    lastUsedAt: Timestamp.now(),
    type: 'program', // Distinguish from fellowship suggestions
  };

  const docRef = await addDoc(suggestionsRef, newSuggestion);
  console.log('✅ Created new custom program suggestion:', program.abbreviation);
  return docRef.id;
}

/**
 * Get all active sobriety dates for a user from subcollection
 */
export async function getSobrietyDates(userId: string): Promise<SobrietyDate[]> {
  if (!auth.currentUser) throw new Error('Not authenticated');

  const sobrietyDatesRef = collection(db, `users/${userId}/sobrietyDates`);
  const snapshot = await getDocs(sobrietyDatesRef);

  return snapshot.docs
    .map((doc) => {
      const data = doc.data();
      return {
        id: doc.id,
        label: data.label,
        startDate: data.startDate, // Keep as Timestamp
        definition: data.definition,
        programId: data.programId,
        isActive: data.isActive !== false, // Default true if not set
        history: data.history || [],
      };
    })
    .filter((date) => date.isActive); // Only return active dates
}

/**
 * Add a new sobriety date to user's subcollection
 */
export async function addSobrietyDate(
  userId: string,
  data: SobrietyDateFormData
): Promise<string> {
  if (!auth.currentUser) throw new Error('Not authenticated');

  const sobrietyDatesRef = collection(db, `users/${userId}/sobrietyDates`);

  const docRef = await addDoc(sobrietyDatesRef, {
    label: data.label,
    startDate: Timestamp.fromDate(new Date(data.startDate)),
    definition: data.definition,
    programId: data.programId,
    isActive: true,
    history: [
      {
        startDate: Timestamp.fromDate(new Date(data.startDate)),
      },
    ],
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  console.log('✅ Sobriety date added:', data.label);
  return docRef.id;
}

/**
 * Update an existing sobriety date
 */
export async function updateSobrietyDate(
  userId: string,
  dateId: string,
  data: Partial<SobrietyDateFormData>
): Promise<void> {
  if (!auth.currentUser) throw new Error('Not authenticated');

  const dateRef = doc(db, `users/${userId}/sobrietyDates/${dateId}`);

  const updates: any = {
    updatedAt: serverTimestamp(),
  };

  if (data.label) updates.label = data.label;
  if (data.definition) updates.definition = data.definition;
  if (data.programId) updates.programId = data.programId;
  if (data.startDate) {
    updates.startDate = Timestamp.fromDate(new Date(data.startDate));
  }

  await updateDoc(dateRef, updates);
  console.log('✅ Sobriety date updated:', dateId);
}

/**
 * Delete a sobriety date (soft delete - marks as inactive)
 */
export async function deleteSobrietyDate(userId: string, dateId: string): Promise<void> {
  if (!auth.currentUser) throw new Error('Not authenticated');

  const dateRef = doc(db, `users/${userId}/sobrietyDates/${dateId}`);

  // Soft delete - mark as inactive instead of deleting
  await updateDoc(dateRef, {
    isActive: false,
    updatedAt: serverTimestamp(),
  });

  console.log('✅ Sobriety date soft-deleted:', dateId);
}
